import React from 'react';
import { Box, Typography, Breadcrumbs, Link } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useParams } from 'react-router-dom';
import { useNavigate } from '../../../hooks/useNavigate';
import { RADIUS, READING_WIDTH } from '../../../theme/tokens';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
}

/**
 * Cabeçalho das páginas internas: faixa tingida com breadcrumbs, título (h1) e subtítulo.
 * Links do breadcrumb navegam pela SPA preservando o prefixo /:state/:city.
 */
export default function PageHeader({ title, subtitle, breadcrumbs }: PageHeaderProps) {
  const navigate = useNavigate();
  const params = useParams<{ state?: string; city?: string }>();

  const hrefOf = (path: string) => {
    if (/^https?:\/\//i.test(path)) return path;
    const clean = path.replace(/^\//, '');
    return params.state && params.city ? `/${params.state}/${params.city}/${clean}` : `/${clean}`;
  };

  const handleClick = (path: string) => (e: React.MouseEvent) => {
    if (/^https?:\/\//i.test(path) || e.ctrlKey || e.metaKey || e.shiftKey) return;
    e.preventDefault();
    navigate(path);
  };

  return (
    <Box
      sx={{
        mb: { xs: 3, md: 4 },
        px: { xs: 2.5, md: 4 },
        py: { xs: 3, md: 4 },
        borderRadius: `${RADIUS.block}px`,
        backgroundColor: 'brand.surface',
      }}
    >
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 1.5, color: 'text.secondary', '& .MuiBreadcrumbs-ol': { rowGap: 0.5 } }}
          aria-label="Você está em"
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', '&:hover': { color: 'brand.text' } }}
            href={hrefOf('/')}
            onClick={handleClick('/')}
          >
            <HomeIcon sx={{ fontSize: '1.125rem' }} />
            Início
          </Link>
          {breadcrumbs.map((crumb, index) =>
            crumb.href ? (
              <Link
                key={index}
                underline="hover"
                sx={{ color: 'text.secondary', '&:hover': { color: 'brand.text' } }}
                href={hrefOf(crumb.href)}
                onClick={handleClick(crumb.href)}
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography key={index} color="text.primary" aria-current="page" sx={{ fontSize: 'inherit', fontWeight: 600 }}>
                {crumb.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
      )}

      <Typography variant="h1" component="h1" sx={{ color: 'text.primary' }}>
        {title}
      </Typography>

      {subtitle && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: READING_WIDTH }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}
